/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { useEffect } from "react";

import * as React from "react";
import styled from "styled-components";
import { AppHeader, AppHeaderHeight } from "components/shared/AppHeader";
import { AppFooter } from "components/shared/AppFooter";
import { devices } from "components/shared/Styles";

import splashImage from "./images/splash.svg";
import { PlansContainer } from "components/Plans/Plan";
import { FreePlan } from "components/Plans/FreePlan";
import { PremiumPlan } from "components/Plans/PremiumPlan";
import { Features } from "components/Plans/Features";
import { PurchaseExpressButton } from "components/PurchaseExpress";
import { useSelector } from "react-redux";
import { webComponentShim } from "../shared/WebComponentShim";
import { Subtitle, Title } from "../shared/Splash";
import { PremiumIcon } from "components/shared/PremiumIcon";
import { ExpressCheckoutButtonPosition } from "constants/Analytics";
import { Analytics, AnalyticsEvent } from "constants/Analytics";
import { UpgradeFeatures } from "components/UpgradeFeatures";

export const HomeColumn = styled.div`
  max-width: 1156px;
  margin: auto;
  padding: 0 24px;

  // Fix for SWC bug: https://adobeprovideo.slack.com/archives/CESK60MQD/p1689197352956859
  font-family: adobe-clean, "Source Sans Pro", -apple-system, BlinkMacSystemFont,
    "Segoe UI", Roboto, Ubuntu, "Trebuchet MS", "Lucida Grande", sans-serif;
  -webkit-font-smoothing: antialiased;
`;

const PageContent = styled.div`
  overflow-x: hidden;
  background-color: var(--spectrum-global-color-static-white);
  position: relative;
`;

const Hero = styled.div`
  display: flex;
  flex-direction: row;
  max-width: 1156px;
  margin: calc(${AppHeaderHeight} + 32px) auto 72px auto;
  position: relative;
  color: #1a1a1a;

  ${devices.small} {
    flex-direction: column-reverse;
    align-items: center;
    gap: 32px;
    margin: calc(${AppHeaderHeight} + 32px) auto 0 auto;
  }
`;

export const Graphic = styled.img`
  flex-grow: 1;
  max-width: 50%;

  ${devices.small} {
    max-width: 100%;
  }
`;

export const SplashCopy = styled.div`
  flex-grow: 1;
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  justify-content: center;
  font-size: 18px;

  ${devices.small} {
    width: 100%;
    margin-bottom: 48px;
  }
`;

const FooterContainer = styled.div`
  font-size: 18px;
  margin: 86px auto;
  text-align: center;
`;

const Express = styled.h2`
  font-size: 36px;
  font-weight: 900;
  margin: 8px 0;
`;

const FooterButtonContainer = styled.div`
  margin: 24px 0;
`;

const Button = styled(webComponentShim("sp-button")).attrs({
  size: "xl",
  static: "black",
})`
  padding-left: 10px;
`;

const Footer: React.FC = () => {
  const currentUser = useSelector((state) => state.currentUser);

  return (
    <FooterContainer>
      {I18n.t("plans.footer.upgrade")}
      <Express>{I18n.t("plans.footer.express")}</Express>
      <FooterButtonContainer>
        {currentUser?.hasPremiumAccess ? (
          <Button color={"black"}>
            <PremiumIcon slot="icon" size={20} />
            {I18n.t("plans.premium.button_access")}
          </Button>
        ) : (
          <PurchaseExpressButton
            color={"black"}
            position={ExpressCheckoutButtonPosition.FEATURES_FOOTER}
            size={"xl"}
          >
            <PremiumIcon slot="icon" size={20} />
            {I18n.t("plans.premium.button")}
          </PurchaseExpressButton>
        )}
      </FooterButtonContainer>
    </FooterContainer>
  );
};

export const Plans: React.FC = () => {
  const currentUser = useSelector((state) => state.currentUser);

  useEffect(() => {
    if (currentUser) {
      Analytics.instance.logEvent(AnalyticsEvent.USER_VISIT_FEATURES);
    }
  }, [currentUser]);

  return (
    <>
      <PageContent>
        <AppHeader />
        <HomeColumn>
          <Hero>
            <SplashCopy data-testid="plans-splash-copy">
              <Title>{I18n.t("plans.title")}</Title>
              <Subtitle>{I18n.t("plans.explanatory_subtitle")}</Subtitle>
            </SplashCopy>
            <Graphic src={splashImage} />
          </Hero>
          <PlansContainer>
            <FreePlan />
            <PremiumPlan />
          </PlansContainer>
        </HomeColumn>
        <UpgradeFeatures
          position={ExpressCheckoutButtonPosition.FEATURES_BENTO_BOX}
        />
        <HomeColumn>
          <Features />
          <Footer />
        </HomeColumn>
        <AppFooter />
      </PageContent>
    </>
  );
};
